export function addHours(date: Date, hours: number): Date
{
    return new Date(date.getTime() + hours * 60 * 60 * 1000);
}

function hoursBetween(from: Date, to: Date): number
{
    return (to.getTime() - from.getTime()) / (1000 * 60 * 60);
}

export function wholeHoursBetween(from: Date, to: Date): number
{
    return Math.round(hoursBetween(from, to));
}

export function dateEqual(a: Date | undefined, b: Date | undefined, msDelta: number = 1000): boolean
{
    if (!a?.getTime || !b?.getTime) {
        return a === b; // just compare to undefined
    }
    return (Math.abs(a.getTime() - b.getTime()) < msDelta);
}

export function parseIsoDate(value: any)
{
    if (typeof value !== "string") {
        return new Date(NaN);
    }

    return new Date(value);
}

export function toIsoMinutes(date: Date): string
{
    return date.toISOString().slice(0, 16) + "Z";
}

/**
 * Ensures that an ISO 8601 date-time string explicitly represents UTC.
 *
 * If the value already ends in `Z`, it is returned unchanged.
 * Otherwise `Z` is appended, meaning a timezone-less value is assumed
 * to already represent UTC.
 *
 * Examples:
 *   2026-08-28T10:23          -> 2026-08-28T10:23Z
 *   2026-08-28T10:23:47.806  -> 2026-08-28T10:23:47.806Z
 *   2026-08-28T10:23Z         -> unchanged
 *
 * Values containing an explicit non-UTC timezone offset are rejected,
 * because simply appending `Z` would not convert them to UTC:
 *   2026-08-28T10:23+02:00    -> throws
 *   2026-08-28T10:23-05:00    -> throws
 *
 * Note: This function does not validate whether the input is otherwise
 * a valid ISO 8601 date-time string.
 */
export function ensureUtc(value: string): string
{
    if (/[+-]\d{2}:\d{2}$/.test(value)) {
        throw new Error(`date.ts: ensureUtc: Date-time already contains a timezone offset: ${value}`);
    }

    return value.endsWith("Z") ? value : `${value}Z`;
}
